import { useLoginRequest } from "@features/auth/api/use-login-request";
import { useUpdateTempPasswordRequest } from "@features/auth/api/use-update-temp-password";
import { zodResolver } from "@hookform/resolvers/zod/src/zod.js";
import EyeIcon from "@icons/eye-open.svg?react";
import Logo from "@icons/logo.svg?react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const TemporaryRoute = getRouteApi("/login/_guest/temp");

import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Button } from "@ui/button/button";
import { Input } from "@ui/input/input";
import { z } from "zod";

import { isStrongPassword } from "../../lib/password-rules";
import { PasswordValidation } from "../password-validation/password-validation";
import styles from "./temp-password.module.scss";

const tempPasswordSchema = z
	.object({
		password: z
			.string()
			.min(1, { message: "base.is_required" })
			.refine(isStrongPassword, {
				message: "login.temp_password.requirements_not_met",
			}),
		repeat_password: z.string().min(1, { message: "base.is_required" }),
	})
	.superRefine((data, ctx) => {
		const isEqual = data.password === data.repeat_password;

		if (!isEqual) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "login.temp_password.passwords_not_equal",
				path: ["repeat_password"],
			});
		}
	});

export const TempPassword = () => {
	const { loginMutateCachedData } = useLoginRequest();
	const { updateTempPasswordMutate, updateTempPasswordPending } =
		useUpdateTempPasswordRequest();

	const [passwordInputType, setPasswordInputType] = useState(true);
	const navigate = useNavigate();

	const { strength } = TemporaryRoute.useSearch();

	const { t } = useTranslation();
	const {
		handleSubmit,
		register,
		formState: { errors },
		watch,
	} = useForm<z.infer<typeof tempPasswordSchema>>({
		resolver: zodResolver(tempPasswordSchema),
		mode: "onBlur",
	});

	// `register` returns native `min`/`max` typed as `string | number`, which
	// conflicts with the Input's `min`/`max` (`number`). They are never
	// populated here (no validation config), so drop them from the spread.
	const {
		min: _passwordMin,
		max: _passwordMax,
		...passwordField
	} = register("password");
	const {
		min: _repeatMin,
		max: _repeatMax,
		...repeatPasswordField
	} = register("repeat_password");

	const handleForm = async (data: z.infer<typeof tempPasswordSchema>) => {
		const isValid = tempPasswordSchema.safeParse(data);

		if (isValid.success) {
			updateTempPasswordMutate({
				login: loginMutateCachedData?.credentials?.login ?? "",
				current_password: loginMutateCachedData?.credentials?.password ?? "",
				new_password: data.password,
				confirm_password: data.repeat_password,
			});
		}
	};

	const hasTempLogin = Boolean(loginMutateCachedData?.credentials);

	useEffect(() => {
		if (hasTempLogin) return;
		navigate({ to: "/login" });
	}, [navigate, hasTempLogin]);

	return (
		<div className={styles.container}>
			<div className={styles.icon}>
				<Logo />
			</div>
			<div className={styles.main_wrapper}>
				<div className={styles.main}>
					<h2 className={styles.title}>
						{t("login.temp_password.enter_new_password")}
					</h2>
					<form onSubmit={handleSubmit(handleForm)} className={styles.form}>
						<Input
							data-testid="temp-password-input"
							type={passwordInputType ? "password" : "text"}
							error={Boolean(errors.password)}
							errorText={t(`${errors.password?.message}`)}
							label={t("form.password.label")}
							placeholder={t("form.password.placeholder")}
							id="password"
							endAdornment={
								<EyeIcon
									data-testid="temp-password-visibility-toggle"
									onClick={() => setPasswordInputType((prev) => !prev)}
									className={styles.eye_icon}
								/>
							}
							{...passwordField}
						/>
						<Input
							data-testid="temp-password-repeat-input"
							type={passwordInputType ? "password" : "text"}
							error={Boolean(errors.repeat_password)}
							errorText={t(`${errors.repeat_password?.message}`)}
							label={t("form.repeat_password.label")}
							placeholder={t("form.repeat_password.placeholder")}
							id="repeat_password"
							endAdornment={
								<EyeIcon
									data-testid="temp-password-repeat-visibility-toggle"
									onClick={() => setPasswordInputType((prev) => !prev)}
									className={styles.eye_icon}
								/>
							}
							{...repeatPasswordField}
						/>
						<div>
							<PasswordValidation password={watch().password ?? ""} />
						</div>

						<Button
							data-testid="temp-password-submit-btn"
							disabled={
								strength !== "5" || watch().password !== watch().repeat_password
							}
							isLoading={updateTempPasswordPending}
							type="submit"
						>
							{t("base.save")}
						</Button>
					</form>
				</div>
			</div>
		</div>
	);
};
